// ─────────────────────────────────────────────────────────────────────────────
// Engine Wire Contract — one zod schema per event-taxonomy payload (the `msg` of
// a wa-message-listener-event envelope). The single home of event payload
// shapes; each descriptor in wa-event-relevance.ts points at its schema, and the
// per-event identity spec is DERIVED from the WireJidSchema / WireKeySchema
// leaves here (no hand-written identity tables).
//
// Two kinds:
//  - Listener-lane payloads are `.passthrough()`: WAWeb models carry ~150
//    fields and only the identity fields are declared. Both engines emit the
//    identity fields `id.remote`/`id.participant`/`from.id`/`to`/`author` as
//    wid objects (`{_serialized, user?, server?}`), typed WireWidSchema. Runtime
//    never parses them (processMessage stays lenient).
//  - Webhook payloads are full `.strict()` shapes — the public webhook contract
//    — written from the extension builders (message-webhook-handler.ts) so the
//    pod can build identical payloads.
// ─────────────────────────────────────────────────────────────────────────────

import { z } from 'zod';
import { WireJidSchema, WireKeySchema, WireWidSchema } from '../contract/wire-identity';
import { BtSlimMessageKeySchema } from '../schemas/wa/message-key';
import { BtSlimMessageSchema, BtSlimQuotedMessageSchema } from '../schemas/wa/message';

// ── Listener-lane payloads ───────────────────────────────────────────────────

/** The listener message key: the BtSlimMessageKey fields with `remote` /
 *  `participant` as wids (both engines emit the WAWeb key form). */
const WaListenerMessageKeySchema = BtSlimMessageKeySchema.extend({
  remote: WireWidSchema.optional(),
  participant: WireWidSchema.optional(),
}).passthrough();

/**
 * receivedNewMessage (incoming and own) — ONE projection, one schema: the
 * listener payload is the message ROW (BtSlimMessageSchema, reused — its
 * fields and null rules) with the identity block in the listener's wid form,
 * plus the listener-only additions below. `.strict()`: an undeclared field
 * fails the contract runner. Runtime never parses it (processMessage stays
 * lenient, §4).
 *
 * Row fields follow the ROW's rules (e.g. `caption` / `filename` present, null
 * when none). "Absent, never a filler" applies only to the listener-only
 * additions (`isGroup`, `isLive`, …).
 *
 * Readers: the backend (chatbot / agent-runtime ingress, voice-note
 * transcription, the tools server actions) and the Team Inbox, which the
 * backend relays the payload to and stores as a message row.
 *
 * Not declared (no reader): `webhookType`, `isNewsletter` (the backend derives
 * it from the chat id), `displayName`, `from.name` / `from.displayName`.
 * `to` / `from.id` wids may carry `user`/`server` (the walker rebuilds them).
 */
export const WaListenerMessageSchema = BtSlimMessageSchema.omit({ from: true, author: true }).extend({
  // ── identity (the listener's wid form) ──
  id: WaListenerMessageKeySchema,
  /** Incoming: the chat (the group for groups). Outgoing: self. */
  from: z.object({
    _serialized: WireJidSchema,
    // @7.15 transitional — deleted in plan task 7.15. Agent-runtime ingress
    // reads `from.id._serialized` as the sender wid.
    id: WireWidSchema.optional(),
  }).strict().optional(),
  /** The other side (incoming: self). Agent-runtime routes 1:1 by it. */
  to: WireWidSchema.optional(),
  /** Incoming group: the participant. Incoming DM: absent. Outgoing: self. */
  author: WireWidSchema.optional(),

  // ── listener-only additions (absent when they do not apply) ──
  // The row's `waMessageKey` (Team Inbox reply/forward on a live message),
  // `ack` (own messages: the Team Inbox ticks) and `secret` (R1) are reused.
  /** A reply: the quoted message's bare stanza id (older consumers). */
  quotedStanzaID: z.string().optional(),
  /** A protocol message (revoke / edit): its target key. */
  protocolMessageKey: z.object({ _serialized: WireKeySchema.optional() }).passthrough().optional(),
  /** `true` exactly when the chat is a group (`@g.us`); absent otherwise. */
  isGroup: z.literal(true).optional(),
  /** Media messages only: the download handles, base64 strings. */
  mediaKey: z.string().optional(),
  directPath: z.string().optional(),
  filehash: z.string().optional(),
  encFilehash: z.string().optional(),
  mediaKeyTimestamp: z.number().optional(),
  /** `true` for a live location / live media; absent otherwise. */
  isLive: z.literal(true).optional(),
  /** vcard / multi_vcard only. */
  vcardFormattedName: z.string().optional(),
  vcardList: z.array(z.object({ displayName: z.string().optional(), vcard: z.string().optional() }).passthrough()).optional(),

  // ── deprecated legacy fields (the extension's listener still emits them) ──
  // Declared so the strict runner tolerates them until the public-webhook
  // decision (Noam). @5.0b decide: delete or promote.
  /** @deprecated @5.0b */
  hasMedia: z.boolean().nullish(),
  /** @deprecated @5.0b WAWeb `Msg.links`. */
  links: z.array(z.unknown()).nullish(),
  /** @deprecated @5.0b the legacy quote preview; `quotedMessage` is the contract. */
  quotedMsg: z.object({ body: z.string().nullish(), caption: z.string().nullish(), type: z.string().nullish() }).passthrough().nullish(),
}).strict();

/** receivedMessageRevoked / receivedCiphertextMessageAdded. A revoke carries
 *  the REVOKED message's identity (its key, `type: 'revoked'`), not the revoke
 *  protocol message. */
export const WaListenerRevokedMessageSchema = WaListenerMessageSchema.extend({
  revokeSender: WireJidSchema.optional(),
});

/** receivedMessageAckUpdate — fromMe-gated at the emit. Dropped (no reader):
 *  `sender`, `recipient`, `fromMe`. */
export const WaMessageAckUpdatePayloadSchema = z.object({
  id: z.object({ _serialized: WireKeySchema.optional() }).passthrough(),
  ack: z.number().nullable().optional(),
  chatId: WireJidSchema.optional(),
  /** The ack's event time (s): present when the engine knows it, never invented
   *  (Baileys' status update carries none). */
  t: z.number().optional(),
}).passthrough();

/** receivedMessageRemoved. */
export const WaMessageRemovedPayloadSchema = z.object({
  id: BtSlimMessageKeySchema.passthrough(),
  chatId: WireJidSchema.optional(),
  lastMessage: WaListenerMessageSchema.optional(),
}).passthrough();

/** receivedUnreadCountChanged. */
export const WaUnreadCountChangedPayloadSchema = z.object({
  chatId: WireJidSchema,
  unreadCount: z.number().nullable().optional(),
  /** The change's event time: present when the engine knows it, never invented
   *  (Baileys' chats.update carries none). */
  timestamp: z.number().nullable().optional(),
}).passthrough();

// ── Webhook payloads (strict) ────────────────────────────────────────────────

/** resolveContactDetails — every `*Details` object. `name` may fall back to a
 *  phone label; it is display text, not an identity field. `mobile` is the
 *  person's phone (`+<digits>`) whenever the engine knows it — for a `@c.us` id
 *  it is derivable from the id; absent otherwise. */
const WaWebhookContactDetailsSchema = z.object({
  id: WireJidSchema.optional(),
  name: z.string().optional(),
  mobile: z.string().optional(),
}).strict();

const groupShape = {
  chatId: WireJidSchema.optional(),
  groupId: WireJidSchema.optional(),
  /** The group's subject; ABSENT when unknown (never the group id as a fallback). */
  groupName: z.string().optional(),
  group: z.object({ id: WireJidSchema.optional(), name: z.string().optional() }).strict().optional(),
};

const timestamps = {
  /** Emit time, ms. */
  timestamp: z.number(),
  /** WhatsApp's own time of the event, in SECONDS (WhatsApp's native `t`) —
   *  one unit for every webhook. Undocumented publicly before; seconds chosen
   *  as the native unit. An engine holding a ms value (an edit's
   *  senderTimestampMs, a poll vote's) floors it to seconds. Present when the
   *  engine knows it, never invented (e.g. Baileys' group-participants.update
   *  carries none). */
  whatsappTimestamp: z.number().nullish(),
};

const messageContentShape = {
  body: z.string().nullish(),
  caption: z.string().nullish(),
  hasMedia: z.boolean(),
  mimetype: z.string().nullish(),
  filename: z.string().nullish(),
};

const WaWebhookReactionEntrySchema = z.object({
  text: z.string().nullish(),
  from: WireJidSchema.optional(),
  fromDetails: WaWebhookContactDetailsSchema.optional(),
  fromName: z.string().optional(),
  timestamp: z.number().nullish(),
}).strict();

/** received_reaction_added (payload = message_reaction_webhook). */
export const WaMessageReactionWebhookSchema = z.object({
  webhookType: z.literal('message_reaction_webhook'),
  messageId: WireKeySchema.optional(),
  messageBody: z.string().nullish(),
  ...groupShape,
  messageFrom: WireJidSchema.optional(),
  messageFromDetails: WaWebhookContactDetailsSchema.optional(),
  reactionCount: z.number(),
  reactorNames: z.array(z.string()),
  ...timestamps,
  reaction: WaWebhookReactionEntrySchema,
  reactions: z.array(WaWebhookReactionEntrySchema),
}).strict();

export const WaReplyToMyMessageWebhookSchema = z.object({
  webhookType: z.literal('reply_to_my_message_webhook'),
  ...groupShape,
  replyMessageId: WireKeySchema.optional(),
  replyMessageBody: z.string().nullish(),
  replyMessageType: z.string().nullish(),
  repliedBy: WireJidSchema.optional(),
  repliedByDetails: WaWebhookContactDetailsSchema.optional(),
  quotedStanzaID: z.string().nullish(),
  originalMessage: z.object({
    id: WireKeySchema.optional(),
    body: z.string().nullish(),
    caption: z.string().nullish(),
    type: z.string().nullish(),
    from: WireJidSchema.optional(),
    fromDetails: WaWebhookContactDetailsSchema.optional(),
    fromMe: z.literal(true),
  }).strict(),
  ...timestamps,
}).strict();

const WaWebhookRecipientSchema = z.object({
  id: WireJidSchema.optional(),
  name: z.string().optional(),
  details: WaWebhookContactDetailsSchema.optional(),
  /** The recipient's receipt time (s): present when the engine knows it, never invented. */
  t: z.number().nullish(),
  status: z.enum(['delivered', 'read', 'played', 'unknown']).optional(),
}).strict();

export const WaAckChangedWebhookSchema = z.object({
  webhookType: z.literal('ack_changed_webhook'),
  messageId: WireKeySchema.optional(),
  chatId: WireJidSchema.optional(),
  isGroup: z.boolean(),
  groupId: WireJidSchema.optional(),
  from: WireJidSchema.optional(),
  author: WireJidSchema.optional(),
  to: WireJidSchema.optional(),
  ...messageContentShape,
  text: z.string().optional(),
  messageType: z.string().nullish(),
  ack: z.number(),
  ackLabel: z.string(),
  fromMe: z.boolean(),
  ...timestamps,
  chatName: z.string().optional(),
  groupName: z.string().optional(),
  group: groupShape.group,
  fromDetails: WaWebhookContactDetailsSchema.optional(),
  authorDetails: WaWebhookContactDetailsSchema.optional(),
  toDetails: WaWebhookContactDetailsSchema.optional(),
  triggeredBy: WaWebhookRecipientSchema.optional(),
  triggeredByName: z.string().optional(),
  recipientsDelivered: z.array(WaWebhookRecipientSchema),
  recipientsRead: z.array(WaWebhookRecipientSchema),
  recipientsPlayed: z.array(WaWebhookRecipientSchema),
}).strict();

const senderShape = {
  from: WireJidSchema.optional(),
  fromDetails: WaWebhookContactDetailsSchema.optional(),
  author: WireJidSchema.optional(),
  authorDetails: WaWebhookContactDetailsSchema.optional(),
  fromMe: z.boolean(),
};

export const WaMessageDeletedRevokedWebhookSchema = z.object({
  webhookType: z.literal('message_deleted_revoked_webhook'),
  messageId: WireKeySchema.optional(),
  ...groupShape,
  ...senderShape,
  deletionReason: z.enum(['removed', 'revoked']),
  deletionScope: z.enum(['delete_for_me', 'delete_for_everyone']),
  subtype: z.string().nullish(),
  deletedBy: WireJidSchema.optional(),
  deletedByDetails: WaWebhookContactDetailsSchema.optional(),
  deletedByName: z.string().optional(),
  message: z.object({ type: z.string().nullish(), ...messageContentShape }).strict(),
  ...timestamps,
}).strict();

export const WaMessageEditedWebhookSchema = z.object({
  webhookType: z.literal('message_edited_webhook'),
  messageId: WireKeySchema.optional(),
  ...groupShape,
  ...senderShape,
  editedField: z.enum(['body', 'caption', 'unknown']),
  previousValue: z.string().optional(),
  newValue: z.string().optional(),
  ...messageContentShape,
  messageType: z.string().nullish(),
  ...timestamps,
}).strict();

const WaPollOptionRefSchema = z.object({
  localId: z.union([z.number(), z.string()]),
  name: z.string().optional(),
}).strict();

const WaPollVoteSchema = z.object({
  voter: WireJidSchema.optional(),
  voterDetails: WaWebhookContactDetailsSchema.optional(),
  voterName: z.string().optional(),
  /** Backwards-compatible alias of selectedOptionLocalIds. */
  selectedOptions: z.array(z.union([z.number(), z.string()])),
  selectedOptionLocalIds: z.array(z.union([z.number(), z.string()])),
  selectedOptionDetails: z.array(WaPollOptionRefSchema),
  selectedOptionNames: z.array(z.string()),
  /** The vote's WhatsApp time in SECONDS (see `timestamps`). */
  whatsappTimestamp: z.number().nullish(),
}).strict();

export const WaPollVoteWebhookSchema = z.object({
  webhookType: z.literal('poll_vote_webhook'),
  messageId: WireKeySchema.optional(),
  ...groupShape,
  pollName: z.string().nullish(),
  pollOptions: z.array(WaPollOptionRefSchema),
  voteCount: z.number(),
  votes: z.array(WaPollVoteSchema),
  latestVote: WaPollVoteSchema.optional(),
  ...timestamps,
}).strict();

// Group (gp2) webhooks share one base; each event adds its own fields.
const groupEventBase = {
  ...groupShape,
  author: WireJidSchema.optional(),
  authorDetails: WaWebhookContactDetailsSchema,
  participants: z.array(WaWebhookContactDetailsSchema),
  participantNames: z.array(z.string()),
  subtype: z.string().nullish(),
  ...timestamps,
};

export const WaParticipantJoinedViaLinkWebhookSchema = z.object({
  webhookType: z.literal('participant_joined_via_link_webhook'),
  ...groupEventBase,
  joinedParticipants: z.array(WaWebhookContactDetailsSchema),
  joinedParticipantNames: z.array(z.string()),
  joinedParticipant: WaWebhookContactDetailsSchema.optional(),
  joinedParticipantName: z.string().optional(),
}).strict();

export const WaParticipantAddedByAdminWebhookSchema = z.object({
  webhookType: z.literal('participant_added_by_admin_webhook'),
  ...groupEventBase,
  addedParticipants: z.array(WaWebhookContactDetailsSchema),
  addedParticipantNames: z.array(z.string()),
  addedParticipant: WaWebhookContactDetailsSchema.optional(),
  addedParticipantName: z.string().optional(),
}).strict();

export const WaParticipantLeftGroupWebhookSchema = z.object({
  webhookType: z.literal('participant_left_group_webhook'),
  ...groupEventBase,
  leftParticipants: z.array(WaWebhookContactDetailsSchema),
  leftParticipantNames: z.array(z.string()),
  leftParticipant: WaWebhookContactDetailsSchema.optional(),
  leftParticipantName: z.string().optional(),
}).strict();

export const WaParticipantKickedFromGroupWebhookSchema = z.object({
  webhookType: z.literal('participant_kicked_from_group_webhook'),
  ...groupEventBase,
  kickedParticipants: z.array(WaWebhookContactDetailsSchema),
  kickedParticipantNames: z.array(z.string()),
  kickedParticipant: WaWebhookContactDetailsSchema.optional(),
  kickedParticipantName: z.string().optional(),
}).strict();

export const WaGroupAdminChangedWebhookSchema = z.object({
  webhookType: z.literal('group_admin_changed_webhook'),
  ...groupEventBase,
  adminAction: z.enum(['promoted_to_admin', 'demoted_from_admin']),
  changedParticipants: z.array(WaWebhookContactDetailsSchema),
  changedParticipantNames: z.array(z.string()),
  changedParticipant: WaWebhookContactDetailsSchema.optional(),
  changedParticipantName: z.string().optional(),
  promotedParticipants: z.array(WaWebhookContactDetailsSchema),
  demotedParticipants: z.array(WaWebhookContactDetailsSchema),
}).strict();

export const WaGroupNameChangedWebhookSchema = z.object({
  webhookType: z.literal('group_name_changed_webhook'),
  ...groupEventBase,
  body: z.string().nullish(),
}).strict();

export const WaGroupDescriptionChangedWebhookSchema = z.object({
  webhookType: z.literal('group_description_changed_webhook'),
  ...groupEventBase,
  body: z.string().nullish(),
}).strict();

export const WaGroupMessagePinnedWebhookSchema = z.object({
  webhookType: z.literal('group_message_pinned_webhook'),
  ...groupEventBase,
  body: z.string().nullish(),
  pinnedMessageId: WireKeySchema.optional(),
  pinType: z.number().nullish(),
}).strict();
